import { yupResolver } from '@hookform/resolvers/yup';
import {
  Dialog,
  DomElementAlignment,
  DomTargetPosition,
  HelpComponent,
  HfCheckbox,
  HfCheckboxGroup,
  type DynamicFormField,
  type ModalDialogButtonSetting,
} from '@myosh/odin-components';
import { useMemo } from 'react';
import { useForm, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import type { ValueTextType } from '../../@types/common';
import i18next from '../../i18n';
import { useAppDispatch } from '../../redux/hooks';
import { submitExportSingleRecordJob } from '../../redux/slices/background-job-slice';

const schema = yup.object().shape({
  appendLinkedRecords: yup.object().nullable(),
  addSignaturePage: yup.object().nullable(),
  specificSectionCheck: yup
    .object({
      name: yup.string(),
      checked: yup.boolean(),
    })
    .nullable(),
  sections: yup
    .array()
    .nullable()
    .of(
      yup.object({
        value: yup.number().required(),
        text: yup.string().required(),
      })
    )
    .when('specificSectionCheck.checked', {
      is: true,
      then: (schema) =>
        schema.min(1, i18next.t('section-selected-error')).required(i18next.t('section-selected-error')),
      otherwise: (schema) => schema,
    }),
});

interface RecordPdfExportModalProps {
  isVisible: boolean;
  recordId: string | undefined;
  recordTitle: string | undefined;
  hide: () => void;
  formGroups?: DynamicFormField[];
}

function RecordPdfExportModal({ isVisible, recordId, recordTitle, hide, formGroups }: RecordPdfExportModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const sectionsCheckboxValues = useMemo(() => {
    return formGroups
      ?.filter(
        (item) =>
          (item.fieldType !== 'CUSTOM' && item.fields && item.fields.length > 0) || item.customFieldType === 'HIERARCHY'
      )
      .map((item) => {
        return { text: item.caption, value: item.customFieldType === 'HIERARCHY' ? -1 : item.id };
      });
  }, [formGroups]);

  const { control, handleSubmit, reset, watch } = useForm<FieldValues>({
    resolver: yupResolver(schema) as never,
    mode: 'onSubmit',
  });

  const specificSectionCheckValue = watch('specificSectionCheck')?.checked as boolean;

  const resetAndHideForm = () => {
    reset();
    hide();
  };

  const onRecordExport = (data: FieldValues) => {
    if (!recordId || !recordTitle) return;

    dispatch(
      submitExportSingleRecordJob({
        formName: recordTitle,
        format: 'PDF',
        recordId,
        appendRecordlinksAsSeparatePages: data.appendRecordlinksAsSeparatePages?.checked ?? false,
        appendSignaturePage: data.appendSignaturePage?.checked ?? false,
        sectionIds: specificSectionCheckValue
          ? (data.sections as ValueTextType[])
              ?.filter((section) => section.value !== -1)
              .map((section) => section.value)
              .join(',')
          : undefined,
        includeHierarchy: specificSectionCheckValue
          ? (data.sections as ValueTextType[]).some((item) => item.value === -1)
          : undefined,
      })
    );

    resetAndHideForm();
  };

  const exportPdfModalButtons: ModalDialogButtonSetting[] = [
    { name: 'export', text: t('export'), type: 'primary', form: 'recordPdfExport', closeModalOnClick: false },
    { name: 'cancel', text: t('cancel'), onClick: resetAndHideForm },
  ];

  return (
    <>
      {isVisible && (
        <Dialog
          header={t('record-pdf-export-config')}
          visible={isVisible}
          onClose={resetAndHideForm}
          buttons={exportPdfModalButtons}
          buttonType="submit"
        >
          <form id="recordPdfExport" className="mt-4 space-y-2" onSubmit={handleSubmit(onRecordExport)}>
            <HfCheckbox
              control={control}
              name="appendRecordlinksAsSeparatePages"
              label={t('record-pdf-export-linked-records')}
            />
            <div className="flex items-center justify-between">
              <HfCheckbox control={control} name="appendSignaturePage" label={t('record-pdf-export-signature-page')} />
              <HelpComponent
                description={t('record-pdf-export-signature-info')}
                tooltipAlignment={DomElementAlignment.TopRight}
                tooltipTargetPosition={DomTargetPosition.TopLeft}
                tooltipZIndexCheck
              />
            </div>
            <HfCheckbox control={control} name="specificSectionCheck" label={t('section-to-export')} />
            {specificSectionCheckValue && (
              <HfCheckboxGroup
                control={control}
                data={sectionsCheckboxValues}
                name="sections"
                id="sections"
                label={t('section-to-include')}
                textField="text"
                valueField="value"
                checkboxGroupStyling="h-52 custom-scroll custom-y-scroll-always-visible"
              />
            )}
          </form>
        </Dialog>
      )}
    </>
  );
}

export default RecordPdfExportModal;
